'use client';

import { useEffect, useState, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuthContext } from '@/context/Auth';
import { useProfileContext } from '@/context/Profile';
import { isUserBuyer } from '@/utils/profile';
import BuyerVerificationModal from '@/components/pricing/BuyerVerificationModal';
import EmailInputModal from './EmailInputModal'; // Add this import

export default function PricingButtonHandlers() {
  const { state: { isVerified }, dispatch } = useAuthContext();
  const { userRole, isLoading: profileLoading } = useProfileContext();
  const [showBuyerVerification, setShowBuyerVerification] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // State for email input modal (replaces browser prompt)
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [currentButton, setCurrentButton] = useState<HTMLButtonElement | null>(null);

  // Handler for payment button click
  const handlePaymentClick = useCallback(async (e: Event, productType: string) => {
    e.preventDefault();
    console.log('🔘 Payment button clicked');

    // Prevent multiple clicks while processing
    if (isProcessing) {
      console.log('⏳ Already processing, please wait...');
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;

    // Check if user is logged in
    if (!user || !isVerified) {
      console.log('❌ User not logged in, opening auth modal');
      dispatch({ type: 'OPEN_MODAL', payload: { defaultTab: 'login' } });
      return;
    }

    const button = e.target as HTMLButtonElement;
    const originalText = button.textContent;

    // Wait for profile to load before checking buyer status
    if (profileLoading) {
      console.log('⏳ Profile still loading, please wait...');
      button.textContent = 'Loading profile...';
      button.disabled = true;

      // Wait a bit for profile to load, then restore button
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
      return;
    }

    // Check if user is a buyer - CRITICAL: Do this check BEFORE any state updates
    const isBuyer = isUserBuyer(userRole);
    console.log('🔍 User role check:', { userRole, isBuyer });

    // If NOT a buyer, show modal and return immediately - DO NOT set processing state
    if (!isBuyer) {
      console.log('❌ User is not a buyer, showing verification modal');
      setShowBuyerVerification(true);
      return;
    }

    // CRITICAL: Only buyers reach this point
    console.log('✅ User is a buyer, proceeding to payment');

    // Set processing state ONLY for buyers to prevent multiple submissions
    setIsProcessing(true);

    // Only proceed to payment if user is verified as a buyer
    try {
      console.log('📤 Creating payment session for product:', productType);
      button.textContent = 'Processing...';
      button.disabled = true;

      const token = await user.getIdToken();
      const response = await fetch('/api/payment/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          product_type: productType || 'doc_review',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create checkout session');
      }

      const data = await response.json();
      if (data.url) {
        console.log('✅ Redirecting to Stripe checkout');
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('❌ Payment error:', error);
      alert(error instanceof Error ? error.message : 'Failed to process payment');
      button.textContent = originalText;
      button.disabled = false;
      setIsProcessing(false);
    }
  }, [isVerified, userRole, profileLoading, dispatch, isProcessing]);

  // Handler for email submission from modal (same logic as before, just moved here)
  const handleEmailSubmit = useCallback(async (email: string) => {
    // Same validation as before
    if (!email || !email.includes('@')) {
      if (email !== null && email !== '') {
        alert('Please enter a valid email address.');
      }
      return;
    }

    const button = currentButton;
    const planName = currentPlanName;

    if (!button) return;

    const originalText = button.textContent;

    // Close modal immediately
    setEmailModalOpen(false);

    try {
      console.log('📤 Submitting to waitlist for plan:', planName);
      button.textContent = 'Submitting...';
      button.disabled = true;

      const response = await fetch('/api/wishlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          service: `pricing-${planName?.toLowerCase()}`
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      if (data.isExisting) {
        alert(`You're already on the waitlist for the ${planName} plan!`);
      } else {
        alert(`Success! You've been added to the ${planName} plan waitlist. We'll notify you when it's available.`);
      }
    } catch (error) {
      console.error('❌ Waitlist error:', error);
      alert(error instanceof Error ? error.message : 'Failed to join waitlist');
    } finally {
      button.textContent = originalText;
      button.disabled = false;
      setCurrentButton(null);
      setCurrentPlanName('');
    }
  }, [currentButton, currentPlanName]);

  useEffect(() => {
    console.log('🚀 PricingButtonHandlers: Initializing...');

    function initPricingPaymentButtons() {
      const paymentButtons = document.querySelectorAll('.pricing-payment-btn');
      console.log('💳 Found', paymentButtons.length, 'payment buttons');

      paymentButtons.forEach((button) => {
        console.log('✅ Attaching payment handler to button:', button.id);

        const productType = button.getAttribute('data-product-type') || 'doc_review';
        const handler = (e: Event) => handlePaymentClick(e, productType);

        // Remove existing listeners and add new one
        button.removeEventListener('click', handler as EventListener);
        button.addEventListener('click', handler as EventListener);
      });
    }

    function initPricingWaitlistButtons() {
      const waitlistButtons = document.querySelectorAll('.pricing-waitlist-btn');
      console.log('📋 Found', waitlistButtons.length, 'waitlist buttons');

      waitlistButtons.forEach((button) => {
        console.log('✅ Attaching waitlist handler to button:', button.id);

        // Remove existing listeners to prevent duplicates
        const newButton = button.cloneNode(true) as HTMLButtonElement;
        button.parentNode?.replaceChild(newButton, button);

        newButton.addEventListener('click', function (e) {
          e.preventDefault();
          console.log('🔘 Waitlist button clicked');

          const planName = this.getAttribute('data-plan-name') || '';

          // Store button reference and plan name, then open modal
          setCurrentButton(this);
          setCurrentPlanName(planName);
          setEmailModalOpen(true);
        });
      });
    }

    // Initialize immediately
    console.log('📄 Initializing pricing buttons immediately...');
    initPricingPaymentButtons();
    initPricingWaitlistButtons();

    // Also initialize after a short delay to catch dynamically loaded content
    const timeout = setTimeout(() => {
      console.log('⏰ Re-initializing pricing buttons after delay...');
      initPricingPaymentButtons();
      initPricingWaitlistButtons();
    }, 500);

    return () => clearTimeout(timeout);
  }, [handlePaymentClick]);

  return (
    <>
      {/* Buyer Verification Modal - same as before */}
      {showBuyerVerification && (
        <BuyerVerificationModal
          opened={showBuyerVerification}
          onClose={() => {
            console.log('🚪 Buyer verification modal closed');
            setShowBuyerVerification(false);
            setIsProcessing(false);
          }}
        />
      )}

      {/* Email Input Modal - replaces browser prompt() */}
      <EmailInputModal
        isOpen={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setCurrentButton(null);
          setCurrentPlanName('');
        }}
        onSubmit={handleEmailSubmit}
        planName={currentPlanName}
      />
    </>
  );
}